import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import {
  SourceTypeIds,
  toSourceTypeId,
  type SourceTypeId,
} from "@onetasklist/shared/sources/source-settings";
import { getCdsSourceSettingsValidationMessage } from "@onetasklist/shared/sources/cds";
import { getJiraSourceSettingsValidationMessage } from "@onetasklist/shared/sources/jira";
import { getPlannerSourceSettingsValidationMessage } from "@onetasklist/shared/sources/planner";
import { getSharePointSourceSettingsValidationMessage } from "@onetasklist/shared/sources/sharepoint";
import { getSlackSourceSettingsValidationMessage } from "@onetasklist/shared/sources/slack";
import { getTrelloSourceSettingsValidationMessage } from "@onetasklist/shared/sources/trello";
import { getZendeskSourceSettingsValidationMessage } from "@onetasklist/shared/sources/zendesk";
import type { ConnectionEditState, ConnectionEditStateUpdater } from "./Connections.ViewModel";
import type { Status } from "@onetasklist/shared";
import { AltusSourceSettings } from "./source-settings/AltusSourceSettings";
import { AsanaSourceSettings } from "./source-settings/AsanaSourceSettings";
import { CdsSourceSettings } from "./source-settings/CDSSourceSettings";
import { ClickUpSourceSettings } from "./source-settings/ClickUpSourceSettings";
import { MondaySourceSettings } from "./source-settings/MondaySourceSettings";
import { GoogleCalendarSourceSettings } from "./source-settings/GoogleCalendarSourceSettings";
import { GoogleDriveSourceSettings } from "./source-settings/GoogleDriveSourceSettings";
import { GoogleTasksSourceSettings } from "./source-settings/GoogleTasksSourceSettings";
import { ConfluenceSourceSettings } from "./source-settings/ConfluenceSourceSettings";
import { GmailSourceSettings } from "./source-settings/GmailSourceSettings";
import { GitHubSourceSettings } from "./source-settings/GitHubSourceSettings";
import { GitLabSourceSettings } from "./source-settings/GitLabSourceSettings";
import { JiraSourceSettings } from "./source-settings/JiraSourceSettings";
import { NotionSourceSettings } from "./source-settings/NotionSourceSettings";
import { OneNoteSourceSettings } from "./source-settings/OneNoteSourceSettings";
import { PlannerSourceSettings } from "./source-settings/PlannerSourceSettings";
import { Project4TWSourceSettings } from "./source-settings/Project4TWSourceSettings";
import { SharePointSourceSettings } from "./source-settings/SharePointSourceSettings";
import { SlackSourceSettings } from "./source-settings/SlackSourceSettings";
import { SalesforceSourceSettings } from "./source-settings/SalesforceSourceSettings";
import { TodoistSourceSettings } from "./source-settings/TodoistSourceSettings";
import { TrelloSourceSettings } from "./source-settings/TrelloSourceSettings";
import { OutlookCalendarSourceSettings } from "./source-settings/OutlookCalendarSourceSettings";
import { OutlookMailSourceSettings } from "./source-settings/OutlookMailSourceSettings";
import { OutlookTasksSourceSettings } from "./source-settings/OutlookTasksSourceSettings";
import { VstsSourceSettings } from "./source-settings/VstsSourceSettings";
import { ZendeskSourceSettings } from "./source-settings/ZendeskSourceSettings";

export interface SourceSettingsEditorProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

interface SourceSettingsEditorRegistration {
  component: ComponentType<SourceSettingsEditorProps>;
  getValidationMessage?: (
    editingConnection: ConnectionEditState,
    t: (key: string) => string,
  ) => string;
}

const sourceSettingsEditors = new Map<SourceTypeId, SourceSettingsEditorRegistration>();

const registerSourceSettingsEditor = (
  sourceTypeId: SourceTypeId,
  editor: ComponentType<SourceSettingsEditorProps>,
  getValidationMessage?: SourceSettingsEditorRegistration["getValidationMessage"],
): void => {
  sourceSettingsEditors.set(sourceTypeId, {
    component: editor,
    getValidationMessage,
  });
};

registerSourceSettingsEditor(SourceTypeIds.Asana, AsanaSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.Altus, AltusSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.AltusLists, CdsSourceSettings, getCdsSourceSettingsValidationMessage);
registerSourceSettingsEditor(SourceTypeIds.Cds, CdsSourceSettings, getCdsSourceSettingsValidationMessage);
registerSourceSettingsEditor(SourceTypeIds.Salesforce, SalesforceSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.ClickUp, ClickUpSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.Monday, MondaySourceSettings);
registerSourceSettingsEditor(SourceTypeIds.GoogleCalendar, GoogleCalendarSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.GoogleDrive, GoogleDriveSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.GoogleTasks, GoogleTasksSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.Confluence, ConfluenceSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.Gmail, GmailSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.GitHub, GitHubSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.GitLab, GitLabSourceSettings);
registerSourceSettingsEditor(
  SourceTypeIds.Jira,
  JiraSourceSettings,
  getJiraSourceSettingsValidationMessage,
);
registerSourceSettingsEditor(SourceTypeIds.MSProject4TW, Project4TWSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.Notion, NotionSourceSettings);
registerSourceSettingsEditor(SourceTypeIds.OneNote, OneNoteSourceSettings);
registerSourceSettingsEditor(
  SourceTypeIds.MicrosoftPlanner,
  PlannerSourceSettings,
  getPlannerSourceSettingsValidationMessage,
);
registerSourceSettingsEditor(
  SourceTypeIds.SharePoint,
  SharePointSourceSettings,
  getSharePointSourceSettingsValidationMessage,
);
registerSourceSettingsEditor(
  SourceTypeIds.Slack,
  SlackSourceSettings,
  getSlackSourceSettingsValidationMessage,
);
registerSourceSettingsEditor(SourceTypeIds.Todoist, TodoistSourceSettings);
registerSourceSettingsEditor(
  SourceTypeIds.Trello,
  TrelloSourceSettings,
  getTrelloSourceSettingsValidationMessage,
);
registerSourceSettingsEditor(
  SourceTypeIds.OutlookCalendar,
  OutlookCalendarSourceSettings,
);
registerSourceSettingsEditor(SourceTypeIds.OutlookMail, OutlookMailSourceSettings);
registerSourceSettingsEditor(
  SourceTypeIds.OutlookTasks,
  OutlookTasksSourceSettings,
);
registerSourceSettingsEditor(SourceTypeIds.Vsts, VstsSourceSettings);
registerSourceSettingsEditor(
  SourceTypeIds.Zendesk,
  ZendeskSourceSettings,
  getZendeskSourceSettingsValidationMessage,
);

export const getSourceSettingsEditor = (
  sourceTypeId: string,
): ComponentType<SourceSettingsEditorProps> | null => {
  const normalizedSourceTypeId = toSourceTypeId(sourceTypeId);
  if (!normalizedSourceTypeId) {
    return null;
  }

  return sourceSettingsEditors.get(normalizedSourceTypeId)?.component || null;
};

export const getSourceSettingsValidationMessage = (
  editingConnection: ConnectionEditState | null,
  t: (key: string) => string,
): string => {
  if (!editingConnection) {
    return "";
  }

  const normalizedSourceTypeId = toSourceTypeId(editingConnection.sourceTypeId);
  if (!normalizedSourceTypeId) {
    return "";
  }

  return (
    sourceSettingsEditors
      .get(normalizedSourceTypeId)
      ?.getValidationMessage?.(editingConnection, t) || ""
  );
};

/**
 * What must be fixed in the source's own settings before the connection can be saved, translated,
 * or "" when nothing needs fixing.
 */
export const useSourceSettingsValidationMessage = (
  editingConnection: ConnectionEditState | null,
): string => {
  const { t } = useTranslation();
  return getSourceSettingsValidationMessage(editingConnection, t);
};
